import { useAuth } from "../contexts/AuthContext";
import { deduplicatedRequest } from "../utils/common";

var API_URL = process.env.REACT_APP_API_URL;

// Define the types for the request function
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface ApiRequestOptions {
  method?: HttpMethod;
  body?: Record<string, unknown> | null;
  customHeaders?: HeadersInit;
}

// Standardized API response envelope for new APIs
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  status_code: number;
  data: T;
  meta?: Record<string, unknown>;
}

interface ApiRequestResult<T> {
  data: T | null;
  error: string | null;
}

const useApi = () => {
  const { userAuthToken, logout } = useAuth();

  const performFetch = async <T>(
    path: string,
    { method = "GET", body = null, customHeaders = {} }: ApiRequestOptions = {}
  ): Promise<ApiRequestResult<T>> => {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Authorization: userAuthToken || "",
      ...customHeaders,
    };

    const config: RequestInit = {
      method,
      headers,
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_URL}${path}`, config);

      // Handle unauthorized separately
      if (response.status === 401) {
        logout();
        return { data: null, error: "Unauthorized. Please log in again." };
      }

      const contentType = response.headers.get("Content-Type");
      let data: any = null;

      if (contentType?.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        const errorMessage =
          (typeof data === "object" && (data?.message || data?.error)) ||
          data ||
          "API request failed";
        return { data: null, error: errorMessage };
      }

      // --- Standardized response unwrapping logic ---
      // If the response matches the new envelope (has 'success' and 'data'), unwrap 'data'.
      if (
        typeof data === "object" &&
        data !== null &&
        "success" in data &&
        "data" in data
      ) {
        return { data: data.data as T, error: null };
      }
      // Otherwise, return the data as-is (for legacy APIs)
      return { data: data as T, error: null };
    } catch (err: any) {
      console.error("API request error:", err);
      return { data: null, error: err.message || "Unknown error" };
    }
  };

  const apiRequest = async <T>(
    path: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiRequestResult<T>> => {
    const { method = "GET" } = options;

    // For GET requests, use the deduplication utility.
    if (method === "GET") {
      const key = `${method}-${path}`;
      return deduplicatedRequest(key, () => performFetch<T>(path, options));
    }

    // For all other methods (POST, PUT, DELETE, etc.), perform the fetch directly.
    return performFetch<T>(path, options);
  };

  return { apiRequest };
};

export default useApi;
